import React, { useMemo, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ShoppingCart, X, Plus, Minus, ShieldCheck, Truck, RotateCcw, Filter, ChevronDown } from 'lucide-react';
// --- Types & Data ---
type Product = {
  id: number;
  name: string;
  price: number;
  category: 'Gym Clothing' | 'Digital Downloads';
  image: string;
  badge?: string;
  sizes?: string[];
};
const PRODUCTS: Product[] = [{
  id: 1,
  name: 'Clarity Tank Top',
  price: 349,
  category: 'Gym Clothing',
  image: 'https://images.unsplash.com/photo-1518310383802-640c2de311b2?auto=format&fit=crop&q=80&w=800',
  sizes: ['XS', 'S', 'M', 'L', 'XL']
}, {
  id: 2,
  name: 'Power Leggings',
  price: 549,
  category: 'Gym Clothing',
  image: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?auto=format&fit=crop&q=80&w=800',
  sizes: ['XS', 'S', 'M', 'L']
}, {
  id: 3,
  name: 'Movement Sports Bra',
  price: 399,
  category: 'Gym Clothing',
  image: 'https://images.unsplash.com/photo-1608228079968-c7681eaef81e?auto=format&fit=crop&q=80&w=800',
  sizes: ['S', 'M', 'L', 'XL']
}, {
  id: 4,
  name: 'Clarity Matching Set',
  price: 799,
  category: 'Gym Clothing',
  image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&q=80&w=800',
  badge: 'BESTSELLER',
  sizes: ['XS', 'S', 'M', 'L']
}, {
  id: 5,
  name: 'Recovery Hoodie',
  price: 599,
  category: 'Gym Clothing',
  image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?auto=format&fit=crop&q=80&w=800',
  sizes: ['S', 'M', 'L', 'XL']
}, {
  id: 6,
  name: 'Flex Shorts',
  price: 349,
  category: 'Gym Clothing',
  image: 'https://images.unsplash.com/photo-1538805060514-97d9cc17730c?auto=format&fit=crop&q=80&w=800',
  sizes: ['XS', 'S', 'M', 'L']
}, {
  id: 7,
  name: 'The Clarity Planner (Digital)',
  price: 149,
  category: 'Digital Downloads',
  image: 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?auto=format&fit=crop&q=80&w=800'
}, {
  id: 8,
  name: '30-Day Fitness Guide',
  price: 199,
  category: 'Digital Downloads',
  image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&q=80&w=800'
}, {
  id: 9,
  name: 'Meal Prep Masterclass Guide',
  price: 179,
  category: 'Digital Downloads',
  image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=800'
}, {
  id: 10,
  name: 'Business Compliance Checklist',
  price: 99,
  category: 'Digital Downloads',
  image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=800'
}, {
  id: 11,
  name: 'Weekly Wellness Template Pack',
  price: 129,
  category: 'Digital Downloads',
  image: 'https://images.unsplash.com/photo-1512314889357-e157c22f938d?auto=format&fit=crop&q=80&w=800'
}, {
  id: 12,
  name: 'Goal Setting Workshop Workbook',
  price: 149,
  category: 'Digital Downloads',
  image: 'https://images.unsplash.com/photo-1456406644174-8ddd4cd52a06?auto=format&fit=crop&q=80&w=800',
  badge: 'NEW'
}];
// --- Animations ---
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function Shop() {
  // --- State ---
  const [cart, setCart] = useState<{
    product: Product;
    quantity: number;
    size?: string;
  }[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [wishlist, setWishlist] = useState<number[]>([]);
  // Filters
  const [activeCategory, setActiveCategory] = useState<'All' | 'Gym Clothing' | 'Digital Downloads'>('All');
  const [activeSize, setActiveSize] = useState<string>('All');
  const [activePrice, setActivePrice] = useState<string>('All');
  const [sortBy, setSortBy] = useState<string>('New Arrivals');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  // --- Handlers ---
  const toggleWishlist = (id: number) => {
    setWishlist(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item => item.product.id === product.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      }
      return [...prev, {
        product,
        quantity: 1,
        size: product.sizes?.[0]
      }];
    });
    setIsCartOpen(true);
  };
  const updateQuantity = (id: number, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.product.id === id) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return {
          ...item,
          quantity: newQuantity
        };
      }
      return item;
    }));
  };
  const removeFromCart = (id: number) => {
    setCart(prev => prev.filter(item => item.product.id !== id));
  };
  const cartTotal = cart.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);
  // --- Derived Data ---
  const filteredProducts = useMemo(() => {
    let result = [...PRODUCTS];
    if (activeCategory !== 'All') {
      result = result.filter(p => p.category === activeCategory);
    }
    if (activeSize !== 'All') {
      result = result.filter(p => p.sizes?.includes(activeSize));
    }
    if (activePrice === 'Under R300') {
      result = result.filter(p => p.price < 300);
    } else if (activePrice === 'R300-R600') {
      result = result.filter(p => p.price >= 300 && p.price <= 600);
    } else if (activePrice === 'R600+') {
      result = result.filter(p => p.price > 600);
    }
    if (sortBy === 'Price Low-High') {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'Price High-Low') {
      result.sort((a, b) => b.price - a.price);
    }
    return result;
  }, [activeCategory, activeSize, activePrice, sortBy]);
  return <main className="flex flex-col min-h-screen bg-warmwhite pt-20" data-id="element-692" data-mp="1f2zwcainknkn">
      {/* HERO SECTION */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-693" data-mp="1a2vd4cqd1eqq">
        <div className="absolute inset-0 z-0" data-id="element-694" data-mp="1o2el95j68q31">
          <img src="https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&q=80&w=2070" alt="Woman in activewear" className="w-full h-full object-cover" data-id="element-695" data-mp="1o13hh5pq7ibx" />
          <div className="absolute inset-0 bg-gradient-to-r from-plum/90 via-plum/70 to-transparent" data-id="element-696" data-mp="27ops506klpab" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-697" data-mp="1tpyj3s6kett1">
          <motion.div initial="hidden" animate="visible" variants={fadeUpVariants} className="max-w-xl" data-id="element-698" data-mp="1xq8zlyrzh2m5">
            <span className="inline-block px-4 py-1 bg-gold text-charcoal text-sm font-bold tracking-wider rounded-full mb-6" data-id="element-699" data-mp="1jxanh0rbmvvw">
              NEW DROP
            </span>
            <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-4" data-id="element-700" data-mp="1a2in5p6ctuxf">
              The Clarity Collection
            </h1>
            <p className="text-warmwhite/90 text-lg mb-8 font-body" data-id="element-701" data-mp="110j7b072sww2">
              Premium activewear and digital tools designed for the woman who
              moves with purpose.
            </p>
            <button onClick={() => document.getElementById('shop-grid')?.scrollIntoView({
            behavior: 'smooth'
          })} className="px-8 py-4 bg-fuchsia text-white rounded-full font-medium hover:bg-fuchsia-light transition-colors shadow-lg" data-id="element-702" data-mp="161fd5jazmt44">
              Shop Now
            </button>
          </motion.div>
        </div>
      </section>

      {/* MAIN SHOP AREA */}
      <section id="shop-grid" className="py-16 md:py-24" data-id="element-703" data-mp="12kcdk19v4p6r">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-704" data-mp="xrj8tdee9guo">
          <div className="flex flex-col lg:flex-row gap-12" data-id="element-705" data-mp="xq86li0lkh5o">
            {/* MOBILE FILTER TOGGLE */}
            <div className="lg:hidden flex justify-between items-center mb-4" data-id="element-706" data-mp="sqlsl6cy2wvt">
              <h2 className="font-display text-2xl font-bold text-plum" data-id="element-707" data-mp="nvomo8ayp3n5">
                Shop All
              </h2>
              <button onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-charcoal/10 shadow-sm" data-id="element-708" data-mp="ju317ykd3h1c">
                <Filter className="w-4 h-4" data-id="element-709" data-mp="2vf3u8rfl2koy" />
                <span className="text-sm font-medium" data-id="element-710" data-mp="2vdsk89vjogpe">Filters</span>
              </button>
            </div>

            {/* SIDEBAR FILTERS */}
            <aside className={`lg:w-64 flex-shrink-0 ${isMobileFilterOpen ? 'block' : 'hidden lg:block'}`} data-id="element-711" data-mp="34gto03wxtvpw">
              <div className="sticky top-28 space-y-10" data-id="element-712" data-mp="27rccv9f6rhgt">
                {/* Categories */}
                <div data-id="element-713" data-mp="2bsfofjq7arc8">
                  <h3 className="font-display font-bold text-lg text-charcoal mb-4" data-id="element-714" data-mp="2brw5bzotdwwm">
                    Category
                  </h3>
                  <div className="space-y-3" data-id="element-715" data-mp="2lh5zrm74rrnw">
                    {['All', 'Gym Clothing', 'Digital Downloads'].map(cat => <label key={cat} className="flex items-center gap-3 cursor-pointer group" data-id="element-716" data-mp="1o10kjmis01yv">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${activeCategory === cat ? 'bg-fuchsia border-fuchsia' : 'border-charcoal/30 group-hover:border-fuchsia'}`} data-id="element-717" data-mp="2vwzeof1tf632">
                          {activeCategory === cat && <div className="w-2 h-2 bg-white rounded-sm" data-id="element-718" data-mp="39vfiny8amihi" />}
                        </div>
                        <span className={`text-sm ${activeCategory === cat ? 'text-charcoal font-medium' : 'text-charcoal/70'}`} data-id="element-719" data-mp="2c6nde2nfgg8z">
                          {cat}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Size */}
                {activeCategory !== 'Digital Downloads' && <div data-id="element-720" data-mp="1yh23rivipddm">
                    <h3 className="font-display font-bold text-lg text-charcoal mb-4" data-id="element-721" data-mp="1yga5g0ajd1jn">
                      Size
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-722" data-mp="1qvq4r6prtnkd">
                      {['All', 'XS', 'S', 'M', 'L', 'XL'].map(size => <button key={size} onClick={() => setActiveSize(size)} className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${activeSize === size ? 'bg-plum text-white border-plum' : 'bg-white text-charcoal/70 border-charcoal/20 hover:border-plum'}`} data-id="element-723" data-mp="1gzd1bh4fbvcf">
                          {size}
                        </button>)}
                    </div>
                  </div>}

                {/* Price */}
                <div data-id="element-724" data-mp="t7xubb1pcpuu">
                  <h3 className="font-display font-bold text-lg text-charcoal mb-4" data-id="element-725" data-mp="oaxx7pmtyxfv">
                    Price
                  </h3>
                  <div className="space-y-3" data-id="element-726" data-mp="kaf4zuh0zpd5">
                    {['All', 'Under R300', 'R300-R600', 'R600+'].map(price => <label key={price} className="flex items-center gap-3 cursor-pointer group" data-id="element-727" data-mp="9qle3wioumkf">
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${activePrice === price ? 'border-fuchsia' : 'border-charcoal/30 group-hover:border-fuchsia'}`} data-id="element-728" data-mp="pf0bv7fbpuv">
                            {activePrice === price && <div className="w-3 h-3 bg-fuchsia rounded-full" data-id="element-729" data-mp="jmoh14ld6p8f" />}
                          </div>
                          <span className={`text-sm ${activePrice === price ? 'text-charcoal font-medium' : 'text-charcoal/70'}`} data-id="element-730" data-mp="ok85syfoukem">
                            {price}
                          </span>
                        </label>)}
                  </div>
                </div>
              </div>
            </aside>

            {/* PRODUCT GRID */}
            <div className="flex-1" data-id="element-731" data-mp="206fly047nbsq">
              {/* Sort Bar */}
              <div className="hidden lg:flex justify-between items-center mb-8 pb-4 border-b border-charcoal/10" data-id="element-732" data-mp="205npyo6pbc1x">
                <p className="text-charcoal/60 text-sm" data-id="element-733" data-mp="1v9fdxjnsvyyz">
                  Showing {filteredProducts.length} products
                </p>
                <div className="flex items-center gap-3" data-id="element-734" data-mp="13godsy2zxq4g">
                  <span className="text-sm text-charcoal/60" data-id="element-735" data-mp="13i9df2463z96">Sort by:</span>
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="bg-transparent text-sm font-medium text-charcoal focus:outline-none cursor-pointer" data-id="element-736" data-mp="1hhirnw8tcjai">
                    <option data-id="element-737" data-mp="2nwjf1djrvt5h">New Arrivals</option>
                    <option data-id="element-738" data-mp="2nvrtywnsxdtg">Price Low-High</option>
                    <option data-id="element-739" data-mp="xffq75g8tsk3">Price High-Low</option>
                  </select>
                </div>
              </div>

              {/* Grid */}
              {filteredProducts.length > 0 ? <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8" data-id="element-740" data-mp="17ckhtksuyg8a">
                  {filteredProducts.map(product => <motion.div key={product.id} variants={fadeUpVariants} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col" data-id="element-741" data-mp="1lcbn8x5b6oao">
                      {/* Image Container */}
                      <div className="relative aspect-[4/5] overflow-hidden bg-warmwhite-dark/20" data-id="element-742" data-mp="1z5unsoq1fn0h">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-743" data-mp="243w4x43wlc9b" />
                        {/* Badges */}
                        {product.badge && <div className="absolute top-4 left-4 px-3 py-1 bg-gold text-charcoal text-xs font-bold tracking-wider rounded-full" data-id="element-744" data-mp="3alvx7to39npu">
                            {product.badge}
                          </div>}
                        {/* Wishlist */}
                        <button onClick={() => toggleWishlist(product.id)} className="absolute top-4 right-4 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center text-plum hover:bg-white transition-colors shadow-sm" data-id="element-745" data-mp="2e749wd0jebx">
                          <Heart className={`w-5 h-5 ${wishlist.includes(product.id) ? 'fill-fuchsia text-fuchsia' : ''}`} data-id="element-746" data-mp="q3hw2ocdxydx" />
                        </button>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-1" data-id="element-747" data-mp="107ombv8qfack">
                        <div className="mb-4 flex-1" data-id="element-748" data-mp="107g6pvl85qxo">
                          <p className="text-xs text-charcoal/50 uppercase tracking-wider mb-1" data-id="element-749" data-mp="148i7igusib1r">
                            {product.category}
                          </p>
                          <h3 className="font-display font-bold text-lg text-charcoal mb-2 line-clamp-2" data-id="element-750" data-mp="v8lpuz6ticg7">
                            {product.name}
                          </h3>
                          <p className="text-fuchsia font-bold text-lg" data-id="element-751" data-mp="hax6jvsefdd9">
                            R{product.price}
                          </p>
                        </div>
                        <button onClick={() => addToCart(product)} className="w-full py-3 bg-plum text-white rounded-full font-medium hover:bg-plum-light transition-colors flex items-center justify-center gap-2" data-id="element-752" data-mp="2bnd5ebiv7p83">
                          <ShoppingCart className="w-4 h-4" data-id="element-753" data-mp="t4afc55gts" />
                          Add to Cart
                        </button>
                      </div>
                    </motion.div>)}
                </motion.div> : <div className="text-center py-24 bg-white rounded-2xl border border-charcoal/5" data-id="element-754" data-mp="noub9jto8p73">
                  <p className="text-charcoal/60 mb-4" data-id="element-755" data-mp="iq9ig5gr17so">
                    No products found matching your filters.
                  </p>
                  <button onClick={() => {
                setActiveCategory('All');
                setActiveSize('All');
                setActivePrice('All');
              }} className="text-fuchsia font-medium hover:underline" data-id="element-756" data-mp="wohfsehp6b4d">
                    Clear all filters
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="py-16 bg-white border-t border-charcoal/5" data-id="element-757" data-mp="2cmf1cfy3f9j5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-758" data-mp="2heeunpjnr46r">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-759" data-mp="2hh14xwxxnxso">
            {[{
            icon: ShieldCheck,
            title: 'Secure Checkout',
            desc: '100% protected payments'
          }, {
            icon: Truck,
            title: 'Fast Delivery',
            desc: 'Nationwide shipping in 2-3 days'
          }, {
            icon: RotateCcw,
            title: 'Easy Returns',
            desc: '30-day return policy'
          }].map((badge, i) => {
            const Icon = badge.icon;
            return <div key={i} className="flex flex-col items-center text-center" data-id="element-760" data-mp="3f15icjhclqyq" data-mpi={i}>
                  <div className="w-12 h-12 rounded-full bg-warmwhite flex items-center justify-center mb-4" data-id="element-761" data-mp="13fd2p5aibzwz">
                    <Icon className="w-6 h-6 text-plum" data-id="element-762" data-mp="yh1v717oq1hx" />
                  </div>
                  <h4 className="font-display font-bold text-charcoal mb-1" data-id="element-763" data-mp="tos4vk2nqzf4">
                    {badge.title}
                  </h4>
                  <p className="text-sm text-charcoal/60" data-id="element-764" data-mp="opnyt2b15ht0">{badge.desc}</p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* FLOATING CART BUTTON */}
      <button onClick={() => setIsCartOpen(true)} className="fixed bottom-8 right-8 w-16 h-16 bg-fuchsia text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-fuchsia-light transition-colors z-40 group" data-id="element-765" data-mp="fnxyf6z678j3">
        <ShoppingCart className="w-6 h-6 group-hover:scale-110 transition-transform" data-id="element-766" data-mp="254730r6lrxbt" />
        {cartCount > 0 && <span className="absolute -top-2 -right-2 w-6 h-6 bg-gold text-charcoal text-xs font-bold rounded-full flex items-center justify-center shadow-sm" data-id="element-767" data-mp="2054ag67t7jkq">
            {cartCount}
          </span>}
      </button>

      {/* CART SLIDE-OUT PANEL */}
      <AnimatePresence data-id="element-768" data-mp="2oj6tjvqnv4fz">
        {isCartOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsCartOpen(false)} className="fixed inset-0 bg-charcoal/40 backdrop-blur-sm z-50" data-id="element-769" data-mp="2jnrgv74e3el5" />

            {/* Panel */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-id="element-770" data-mp="20t30gib3mn38">
              {/* Header */}
              <div className="px-6 py-4 border-b border-charcoal/10 flex items-center justify-between bg-warmwhite" data-id="element-771" data-mp="1mlpw5hleefnk">
                <h2 className="font-display text-2xl font-bold text-plum flex items-center gap-2" data-id="element-772" data-mp="1qnunuasad27k">
                  Your Cart{' '}
                  <span className="text-sm font-body font-normal text-charcoal/60 bg-charcoal/5 px-2 py-0.5 rounded-full" data-id="element-773" data-mp="tydk41cv5q8i">
                    {cartCount}
                  </span>
                </h2>
                <button onClick={() => setIsCartOpen(false)} className="p-2 text-charcoal/60 hover:text-fuchsia transition-colors rounded-full hover:bg-charcoal/5" data-id="element-774" data-mp="12w75ih93azgw">
                  <X className="w-6 h-6" data-id="element-775" data-mp="2ef04ut9yq0jg" />
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-776" data-mp="j9r05ec1ywqk">
                {cart.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-4" data-id="element-777" data-mp="f85tiaq43eot">
                    <div className="w-20 h-20 bg-warmwhite rounded-full flex items-center justify-center mb-4" data-id="element-778" data-mp="t7ooe1twwtj6">
                      <ShoppingCart className="w-8 h-8 text-charcoal/20" data-id="element-779" data-mp="o8c49oufe0ka" />
                    </div>
                    <p className="text-lg font-medium text-charcoal" data-id="element-780" data-mp="iq4az68bhb8">
                      Your cart is empty
                    </p>
                    <p className="text-charcoal/60 mb-8" data-id="element-781" data-mp="3rr5bzl0ivv6j">
                      Looks like you haven't added anything yet.
                    </p>
                    <button onClick={() => setIsCartOpen(false)} className="px-8 py-3 bg-plum text-white rounded-full font-medium hover:bg-plum-light transition-colors" data-id="element-782" data-mp="5im8ayexf8qs">
                      Continue Shopping
                    </button>
                  </div> : <div className="space-y-6" data-id="element-783" data-mp="1ofh62algx50b">
                    {cart.map(item => <div key={item.product.id} className="flex gap-4 bg-white" data-id="element-784" data-mp="1t8ip8uspjqpp">
                        <div className="w-24 h-24 rounded-xl overflow-hidden bg-warmwhite flex-shrink-0" data-id="element-785" data-mp="1jhxj8mapfjwr">
                          <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-786" data-mp="277ru4ybgqyed" />
                        </div>
                        <div className="flex-1 flex flex-col justify-between" data-id="element-787" data-mp="1y6k8bfq4vq16">
                          <div data-id="element-788" data-mp="234lrs1rh1rd6">
                            <div className="flex justify-between items-start mb-1" data-id="element-789" data-mp="237rdr84t0d4o">
                              <h4 className="font-medium text-charcoal line-clamp-1 pr-4" data-id="element-790" data-mp="mqdgx4g2d6hd">
                                {item.product.name}
                              </h4>
                              <button onClick={() => removeFromCart(item.product.id)} className="text-charcoal/40 hover:text-fuchsia transition-colors" data-id="element-791" data-mp="1716gfixdour5">
                                <X className="w-4 h-4" data-id="element-792" data-mp="t903e5piv3vx" />
                              </button>
                            </div>
                            {item.size && <p className="text-xs text-charcoal/60 mb-2" data-id="element-793" data-mp="1px6b51hqqcef">
                                Size: {item.size}
                              </p>}
                            <p className="text-fuchsia font-bold" data-id="element-794" data-mp="1gwrcz1joh2ti">
                              R{item.product.price}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 mt-2" data-id="element-795" data-mp="5k6znn2nrrqr">
                            <div className="flex items-center border border-charcoal/20 rounded-full" data-id="element-796" data-mp="k2uuub44lgd">
                              <button onClick={() => updateQuantity(item.product.id, -1)} className="p-1.5 text-charcoal/60 hover:text-plum transition-colors" data-id="element-797" data-mp="2c8h5l8nnqzjg">
                                <Minus className="w-3 h-3" data-id="element-798" data-mp="2pzvxk4zjmb2n" />
                              </button>
                              <span className="w-8 text-center text-sm font-medium" data-id="element-799" data-mp="2uxe27j6j244u">
                                {item.quantity}
                              </span>
                              <button onClick={() => updateQuantity(item.product.id, 1)} className="p-1.5 text-charcoal/60 hover:text-plum transition-colors" data-id="element-800" data-mp="34w3jb9qlnkk1">
                                <Plus className="w-3 h-3" data-id="element-801" data-mp="1ekp5a5tazi7n" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>

              {/* Footer */}
              {cart.length > 0 && <div className="border-t border-charcoal/10 p-6 bg-warmwhite space-y-4" data-id="element-802" data-mp="2mtelq0orv2pj">
                  <div className="flex justify-between items-center text-lg" data-id="element-803" data-mp="30tfl1bpe222w">
                    <span className="text-charcoal/80" data-id="element-804" data-mp="30rckvyuiuskg">Subtotal</span>
                    <span className="font-display font-bold text-2xl text-plum" data-id="element-805" data-mp="2vu2zqve6132f">
                      R{cartTotal}
                    </span>
                  </div>
                  <p className="text-xs text-charcoal/50 text-center" data-id="element-806" data-mp="35k5qeoxte4xl">
                    Shipping and taxes calculated at checkout.
                  </p>
                  <button className="w-full py-4 bg-fuchsia text-white rounded-full font-medium hover:bg-fuchsia-light transition-colors shadow-lg text-lg" data-id="element-807" data-mp="3ji455vg1s3dm">
                    Checkout
                  </button>
                </div>}
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}